// Every CardBlock type from shared/core.ts. Block anatomy adapted from EveryInc/tend
// src/feed/CardView.tsx (MIT, Copyright (c) 2026 Every, Inc.): Tailwind tokens instead of CSS,
// dark mode, editable text with variants/confidence/commitments, research citations (see
// ResearchBlock), the captured task (TaskRefBlock), and the email thread opened in the drawer
// (never transcribed by the model).
import { useRef, type ReactNode } from "react";
import { Check, Circle, Mail, Quote as QuoteIcon } from "lucide-react";
import type { BlockItem, CardBlock, Commitment, Confidence } from "@shared/core";
import { HOLDING_BLOCK_ID } from "@shared/unstick";
import { cn } from "@web/lib/cn";
import { Kbd } from "@web/ui/Kbd";
import { DraftQuestion } from "./DraftQuestion";
import { DraftTools } from "./DraftTools";
import { FormattedText, InlineText, ModelLink } from "./FormattedText";
import { HoldingDayPicker } from "./HoldingDayPicker";
import { IntentChips } from "./IntentChips";
import { safeHref } from "./markdownLite";
import { PlaceholderPrompt } from "./PlaceholderPrompt";
import { ResearchBlock } from "./ResearchBlock";
import { SlotsBlock, type SlotsContext } from "./SlotsBlock";
import { TaskRefBlock } from "./TaskRefBlock";

function Label({ children }: { children: ReactNode }) {
  return <h3 className="mm-kicker mb-1.5">{children}</h3>;
}

function ItemText({ item }: { item: BlockItem }) {
  if (typeof item === "string") return <InlineText text={item} />;
  const href = item.href ? safeHref(item.href) : null;
  return (
    <span className="flex flex-col">
      {href ? (
        <span>
          <ModelLink href={href} label={item.label} />
        </span>
      ) : (
        <span className="text-ink">
          <InlineText text={item.label} />
        </span>
      )}
      {item.detail ? (
        <span className="text-sm text-muted">
          <InlineText text={item.detail} />
        </span>
      ) : null}
    </span>
  );
}

const CONFIDENCE_WORDS: Record<Confidence, string> = {
  HIGH: "Claude is confident in this draft",
  MEDIUM: "Claude is fairly sure about this draft",
  LOW: "Claude is unsure about this draft; worth a careful read",
};

const COMMITMENT_WORDS: Record<Commitment["type"], string> = {
  meeting_time: "Meeting time",
  price: "Price",
  deadline: "Deadline",
  yes_to_request: "Saying yes",
  other: "Promise",
};

function Commitments({ commitments }: { commitments: readonly Commitment[] }) {
  if (!commitments.length) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-sm text-muted">This draft commits you to:</p>
      <ul className="flex flex-col gap-1.5">
        {commitments.map((c, i) => (
          <li
            key={i}
            className={cn(
              "rounded-lg border px-3 py-2 text-sm",
              c.flagged ? "border-note-line bg-note text-note-ink" : "border-line bg-bg text-ink-2",
            )}
          >
            <span className="font-medium">{COMMITMENT_WORDS[c.type]}:</span> {c.value}
            {c.quote ? <span className="text-muted"> · “{c.quote}”</span> : null}
            {c.flagged ? (
              <span className="mt-0.5 block">
                Worth checking{c.flagReason ? `: ${c.flagReason}` : "."}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface EditableProps {
  cardId: string;
  value: string;
  variantIndex: number;
  onChange: (value: string) => void;
  onBlur: () => void;
  onVariant: (index: number) => void;
  readOnly: boolean;
}

function EditableBlock({ block, editable }: { block: CardBlock; editable: EditableProps }) {
  const variants = block.variants ?? [];
  const rows = Math.min(18, Math.max(4, editable.value.split("\n").length + 1));
  // U4: versions labelled by intent show as chips above the draft instead of small tabs.
  const chips = !!block.intents && variants.length > 1;
  const editorRef = useRef<HTMLTextAreaElement>(null);
  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label>{block.label ?? "Draft"}</Label>
        {variants.length > 1 && !chips ? (
          <div role="tablist" aria-label="Draft versions" className="flex items-center gap-1">
            {variants.map((v, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === editable.variantIndex}
                disabled={editable.readOnly}
                onClick={() => editable.onVariant(i)}
                className={cn(
                  "rounded-md px-2 py-0.5 text-xs",
                  i === editable.variantIndex
                    ? "bg-accent-soft font-medium text-accent-text"
                    : "text-muted hover:bg-sunk hover:text-ink",
                )}
              >
                {v.label}
              </button>
            ))}
            <Kbd className="ml-1 hidden sm:inline-flex">V</Kbd>
          </div>
        ) : null}
      </div>
      {/* U5: the holding reply's day, and a way to take it off the card. */}
      {block.id === HOLDING_BLOCK_ID ? (
        <HoldingDayPicker
          cardId={editable.cardId}
          value={editable.value}
          onChange={editable.onChange}
          readOnly={editable.readOnly}
        />
      ) : null}
      {chips ? (
        <IntentChips
          cardId={editable.cardId}
          labels={variants.map((v) => v.label)}
          index={editable.variantIndex}
          onPick={editable.onVariant}
          disabled={editable.readOnly}
        />
      ) : null}
      <textarea
        ref={editorRef}
        data-card-editor={editable.cardId}
        data-block-id={block.id}
        aria-label={block.label ?? "Draft"}
        value={editable.value}
        readOnly={editable.readOnly}
        onChange={(e) => editable.onChange(e.target.value)}
        onBlur={editable.onBlur}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
        rows={rows}
        className={cn(
          "w-full resize-y rounded-lg border border-line-strong bg-bg px-3.5 py-3 text-[15px] leading-relaxed text-ink",
          "focus:border-accent focus:outline-none focus-visible:outline-none",
          editable.readOnly && "opacity-80",
        )}
      />
      <PlaceholderPrompt value={editable.value} editorRef={editorRef} />
      {/* U5: tone and length as a diff; dictation when it is on. */}
      <DraftTools
        cardId={editable.cardId}
        value={editable.value}
        onChange={editable.onChange}
        onSave={editable.onBlur}
        readOnly={editable.readOnly}
      />
      {block.confidence ? (
        <p className="text-sm text-muted">
          {CONFIDENCE_WORDS[block.confidence]}
          {block.confidenceWhy ? `: ${block.confidenceWhy}` : "."}
        </p>
      ) : null}
      {block.commitments?.length ? <Commitments commitments={block.commitments} /> : null}
    </section>
  );
}

/** A long list, or one of links, folds so the card shows the decision first. */
export function checklistFold(block: Pick<CardBlock, "title" | "label" | "items">): {
  fold: boolean;
  links: boolean;
  summary: string;
} {
  const count = block.items?.length ?? 0;
  const links = /link/i.test(`${block.title ?? ""} ${block.label ?? ""}`);
  return {
    fold: count > 3 || (links && count > 0),
    links,
    summary: `${count} ${links ? (count === 1 ? "link" : "links") + " from the email" : count === 1 ? "item" : "items"}`,
  };
}

function ChecklistBlock({ block }: { block: CardBlock }) {
  const { fold, links, summary } = checklistFold(block);
  const list = (
    <ul className="flex flex-col gap-1.5">
      {block.items?.map((item, i) => {
        const checked = typeof item !== "string" && item.checked === true;
        return (
          <li key={i} className="flex items-start gap-2 text-ink-2">
            {checked ? (
              <Check aria-label="Done" className="mt-1 size-4 shrink-0 text-calm" />
            ) : (
              <Circle aria-label="Open" className="mt-1 size-4 shrink-0 text-faint" />
            )}
            <ItemText item={item} />
          </li>
        );
      })}
    </ul>
  );
  if (!fold)
    return (
      <section>
        {block.label ? <Label>{block.label}</Label> : null}
        {list}
      </section>
    );
  return (
    <section>
      {/* The summary says what the label would ("Links from the email"), so only other labels stay. */}
      {block.label && !links ? <Label>{block.label}</Label> : null}
      <details data-testid="folded-list" className="group">
        <summary className="cursor-pointer text-sm text-ink-2 hover:text-ink">{summary}</summary>
        <div className="mt-2">{list}</div>
      </details>
    </section>
  );
}

export interface BlockViewProps {
  block: CardBlock;
  /** The card the block is on (research: "Save answer" keeps it in the Answer library). */
  cardId?: string | undefined;
  editable?: EditableProps | undefined;
  onOpenThread: () => void;
  /** slots: the card the block belongs to (holds are read per card). */
  slotsContext?: SlotsContext | undefined;
}

export function BlockView({ block, cardId, editable, onOpenThread, slotsContext }: BlockViewProps) {
  switch (block.type) {
    case "slots":
      return <SlotsBlock block={block} context={slotsContext} />;
    case "editable_text":
      return editable ? (
        <EditableBlock block={block} editable={editable} />
      ) : (
        <section>
          <Label>{block.label ?? "Draft"}</Label>
          <FormattedText text={block.value ?? block.text} className="text-ink-2" />
        </section>
      );
    case "memo":
      return (
        <section className="rounded-r-lg border-l-[3px] border-memo-line bg-memo px-4 py-3">
          {block.label ? <Label>{block.label}</Label> : null}
          {block.title ? <p className="mb-1 font-medium text-ink">{block.title}</p> : null}
          {/* Runtime memos (e.g. "Researched as") carry their text in `value`. */}
          <FormattedText text={block.text ?? block.value} className="text-ink-2" />
        </section>
      );
    case "evidence":
      return (
        <section>
          <Label>{block.label ?? "From the email"}</Label>
          {block.text ? <FormattedText text={block.text} className="text-ink-2" /> : null}
          {block.items?.length ? (
            <ul className="mt-1 flex list-disc flex-col gap-1 pl-5 text-ink-2 marker:text-calm">
              {block.items.map((item, i) => (
                <li key={i}>
                  <ItemText item={item} />
                </li>
              ))}
            </ul>
          ) : null}
          {block.attribution ? (
            <p className="mt-1.5 text-sm text-muted">— {block.attribution}</p>
          ) : null}
        </section>
      );
    case "options":
      return (
        <section>
          {block.label ? <Label>{block.label}</Label> : null}
          <div className="flex flex-col gap-2">
            {block.items?.map((item, i) => (
              <div key={i} className="rounded-lg border border-line bg-bg px-3 py-2.5">
                <ItemText item={item} />
              </div>
            ))}
          </div>
        </section>
      );
    case "checklist":
      return <ChecklistBlock block={block} />;
    case "diff":
      return (
        <section>
          {block.label ? <Label>{block.label}</Label> : null}
          <div className="grid gap-2 md:grid-cols-2">
            <div className="rounded-lg bg-before px-3 py-2">
              <p className="mm-kicker mb-1">Before</p>
              <pre className="font-mono text-[13px] leading-relaxed break-words whitespace-pre-wrap text-ink-2">
                {block.before}
              </pre>
            </div>
            <div className="rounded-lg bg-after px-3 py-2">
              <p className="mm-kicker mb-1">After</p>
              <pre className="font-mono text-[13px] leading-relaxed break-words whitespace-pre-wrap text-ink">
                {block.after}
              </pre>
            </div>
          </div>
        </section>
      );
    case "clarification":
      return (
        <section className="rounded-lg border border-note-line bg-note px-4 py-3 text-note-ink">
          <p className="mm-kicker mb-1 !text-note-ink">{block.label ?? "Needs your answer"}</p>
          <FormattedText text={block.text} />
          <p className="mt-2 text-sm opacity-80">
            Answer in the dock below (<Kbd>.</Kbd>) and Claude will continue.
          </p>
        </section>
      );
    case "email_thread":
      return (
        <section>
          <button
            type="button"
            onClick={onOpenThread}
            className="inline-flex items-center gap-2 rounded-lg border border-line bg-bg px-3 py-1.5 text-sm text-ink-2 hover:bg-sunk hover:text-ink"
          >
            <Mail aria-hidden className="size-4 text-muted" />
            {block.label ?? "Read the email thread"}
            <Kbd>O</Kbd>
          </button>
        </section>
      );
    case "receipt":
      return (
        <section className="rounded-lg border border-calm-line bg-calm-soft px-4 py-3">
          <p className="mm-kicker mb-1">{block.label ?? "Done"}</p>
          <FormattedText text={block.text} className="text-ink-2" />
        </section>
      );
    case "quote":
      return (
        <figure className="border-l-2 border-line-strong pl-4">
          <QuoteIcon aria-hidden className="mb-1 size-4 text-faint" />
          <blockquote className="font-serif text-[17px] leading-relaxed text-ink">
            <FormattedText text={block.text} />
          </blockquote>
          {block.attribution ? (
            <figcaption className="mt-1 text-sm text-muted">— {block.attribution}</figcaption>
          ) : null}
        </figure>
      );
    case "research":
      return <ResearchBlock block={block} {...(cardId ? { cardId } : {})} />;
    case "draft_question":
      return <DraftQuestion block={block} cardId={cardId} />;
    case "task_ref":
      return <TaskRefBlock block={block} />;
    case "rich_text":
    default:
      return (
        <section>
          {block.label ? <Label>{block.label}</Label> : null}
          {block.title ? <p className="mb-1 font-medium text-ink">{block.title}</p> : null}
          <FormattedText text={block.text} className="text-ink-2" />
        </section>
      );
  }
}
